<template>
  <div>
    <div class="slds-form-element__control">
      <div class="slds-checkbox">
        <input type="checkbox" name="options" :id="'checkbox-unique-id-' + id" v-model="filterEnabled" :disabled="defaultActive || filterMode === 'remove-only'" />
        <label class="slds-checkbox__label" :for="'checkbox-unique-id-' + id">
          <span class="slds-checkbox_faux"></span>
          <span class="slds-form-element__label">{{label}}</span>
          <abbr class="slds-required" title="required" v-if="defaultActive">*</abbr>
        </label>
        <Tooltip :text="tooltip" v-if="tooltip" direction="right" />
      </div>
    </div>

    <!-- 'Included' picklist filter -->
    <Picklist
      :accordion-id="accordionId"
      :disabled="!filterEnabled" :multipicklist="isMultiPicklist" ref="child"
      :hide-picklist="filterMode === 'remove-only'"
      :labels-prefix="labelsPrefix"
      :searchable="true"
      :options="options"
      :validation="validationRules"
      class="slds-m-top--xx-small"
      v-if="filterType === 'included' && (isPicklist || isMultiPicklist)"
      v-model="localValue" />

    <!-- 'Included' text filter -->
    <IncludedFilter :input-type="inputType" :hide-input="filterMode === 'remove-only'" :items-validation="itemsValidation" class="slds-m-top--x-small" :validation="validationRules" :labels-prefix="labelsPrefix" v-if="filterType === 'included' && !(isPicklist || isMultiPicklist)" v-model="localValue" :disabled="!filterEnabled" ref="child" />

    <!-- 'Range filter -->
    <RangeFilter :input-type="inputType" :filterMode="filterMode" :disabled="!filterEnabled" :validation="validationRules" :labels-prefix="labelsPrefix" v-if="filterType === 'range'" :from-label="$t('general.from')" :to-label="$t('general.to')" v-model="localValue" ref="child" />

    <!-- Exact match filter -->
    <Picklist :accordion-id="accordionId" :hide-picklist="filterMode === 'remove-only'" class="slds-m-top--x-small" :validation="validationRules" v-if="filterType === 'exact'" v-model="localValue" :disabled="!filterEnabled" :options="yesNoOptions" ref="child" />

    <template v-if="filterType === 'carAge'">
      <div class="row">
        <div class="row">
          <RadioGroup
            class="slds-m-right--xx-large"
            :disabled="filterMode === 'remove-only' || !filterEnabled"
            :label="$t('campaignDesigner.targetDefinition.rangeSelector')"
            :options="carAgesOptions"
            v-model="localValue.timeRangeSelector"
            @change="resetCarAge" />
        </div>
        <!-- Touchpoint -->
        <div class="row" v-if="localValue.timeRangeSelector === 'touchpoint'">
          <Input
            :disabled="!filterEnabled"
            :label="$t('general.from')"
            :validation="!originalValue ? ['required', 'positive-number', { name: 'less-or-equal-than', param: localValue.range.touchpoint.touchpointTo }, { name: 'less-or-equal-than', param: 240 }] : [
              'required',
              { name: 'bigger-or-equal-than', param: originalValue.range.touchpoint.touchpointFrom },
              { name: 'less-or-equal-than', param: localValue.range.touchpoint.touchpointTo },
              { name: 'less-or-equal-than', param: 240 },
              'positive-number',
            ]"
            class="slds-m-right--small"
            ref="fromRange"
            type="number"
            v-model="localValue.range.touchpoint.touchpointFrom" />
          <Input
            :disabled="!filterEnabled"
            :label="$t('general.to')"
            :validation="!originalValue ? ['required', 'positive-number', { name: 'less-or-equal-than', param: 240 }] : [
              'required',
              { name: 'less-or-equal-than', param: originalValue.range.touchpoint.touchpointTo },
              { name: 'less-or-equal-than', param: 240 },
              'positive-number',
            ]"
            ref="toRange"
            type="number"
            v-model="localValue.range.touchpoint.touchpointTo"  />
        </div>
        <!-- Date -->
        <div class="row" v-if="localValue.timeRangeSelector === 'date'">
          <Input
            class="slds-m-right--small"
            type="date"
            type2="date-carAge"
            ref="fromDate"
            v-model="localValue.range.date.dateFrom"
            :validation="!originalValue ? ['required', { name: 'is-date-before', param: localValue.range.date.dateTo }] :
              [
                { name: 'is-date-after', param: {date: originalValue.range.date.dateFrom, message: 'startDateExceed'} },
                { name: 'is-date-before', param: localValue.range.date.dateTo },
                'required'
              ]"
            :disabled="!filterEnabled"
            :label="$t('general.from')"
          />

          <Input
            ref="toDate"
            type="date"
            type2="date-carAge"
            v-model="localValue.range.date.dateTo"
            :disabled="!filterEnabled"
            :label="$t('general.to')"
            :validation="!originalValue ? ['required'] : [
              { name: 'is-date-before', param: {date: originalValue.range.date.dateTo, message: 'endDateExceed'} },
              'required'
            ]"
            />

        </div>
        <!-- Age -->
        <div v-if="localValue.timeRangeSelector === 'age'">
          <Input
            hide-clear-button
            type="number"
            no-label
            ref="child"
            v-model="localValue.range.age.monthAge"
            :validation="!originalValue ? ['positive-number', 'required', { name: 'less-or-equal-than', param: 240 }] : [
              'positive-number',
              'required',
              { name: 'bigger-or-equal-than', param: originalValue.range.age.monthAge },
              { name: 'less-or-equal-than', param: 240 },
            ]"
            :disabled="!filterEnabled"
            :fixedTextAddonPost="$t('general.months')" />
        </div>
      </div>
    </template>
    <slot v-if="hasAction" name="action"></slot>
  </div>
</template>
<script>
import IncludedFilter from './IncludedFilter'
import Input from '../../slds/Input'
import RadioGroup from '../../slds/RadioGroup'
import Picklist from '../../slds/Picklist'
import RangeFilter from './RangeFilter'
import Tooltip from '../../slds/Tooltip'
export default {
  components: {
    IncludedFilter,
    Input,
    Picklist,
    RadioGroup,
    RangeFilter,
    Tooltip
  },
  methods: {
    getDefaultValue () {
      switch (this.filterType) {
        case 'carAge': {
          return {
            timeRangeSelector: null,
            range: {
              touchpoint: {
                touchpointFrom: null,
                touchpointTo: null
              },
              date: {
                dateFrom: null,
                dateTo: null
              },
              age: {
                monthAge: null
              }
            }
          }
        }
        case 'included':
          return this.isPicklist ? '' : []
        case 'range':
          return []
        case 'exact':
          return ''
        default:
          throw new Error('Unhandled filterType ' + this.filterType)
      }
    },
    resetCarAge () {
      this.localValue.range = this.getDefaultValue().range
    },
    validate () {
      try {
        let valid = true
        Object.values(this.$refs).forEach(child => {
          if (child) {
            child.clearValidationError()
            valid &= child.validate()
          }
        })
        return valid
      } catch (error) {
        console.error(`Error in validation for filter ${this.label}`, error)
      }
    }
  },
  props: {
    accordionId: String,
    defaultActive: Boolean,
    defaultValue: String,
    hasAction: Boolean,
    label: String,
    filterMode: String,
    filterType: String,
    inputType: {
      type: String,
      default: 'text'
    },
    isPicklist: Boolean,
    isMultiPicklist: Boolean,
    itemsValidation: Array,
    labelsPrefix: String,
    options: Array,
    originalValue: Object,
    required: Boolean,
    tooltip: String,
    value: [String, Array, Object, Boolean]
  },
  data () {
    return {
      carAgesOptions: [
        {
          label: this.$t('filters.carAge.touchpoint'),
          value: 'touchpoint'
        },
        {
          label: this.$t('filters.carAge.date'),
          value: 'date'
        },
        {
          label: this.$t('filters.carAge.age'),
          value: 'age'
        }
      ],
      filterEnabled: !!this.value && (
        !!this.value.length || (
          this.filterType === 'carAge' &&
          this.value.timeRangeSelector &&
          this.value.timeRangeSelector.length
        )
      ),
      localValue: this.getDefaultValue(),
      id: Math.ceil(Math.random() * 1000),
      yesNoOptions: [
        {
          label: this.$t('general.yes'),
          value: 'Y'
        },
        {
          label: this.$t('general.no'),
          value: 'N'
        }
      ]
    }
  },
  watch: {
    filterEnabled (newValue, oldValue) {
      if (newValue) {
        if (this.defaultValue) {
          this.localValue = this.defaultValue
        }
      } else {
        this.localValue = this.getDefaultValue()
      }
      Object.values(this.$refs).forEach(child => child && child.clearValidationError())
    },
    localValue (newValue) {
      this.$emit('input', newValue)
    },
    value (value, oldValue) {
      this.localValue = value
      this.filterEnabled = !!value && (
        !!value.length || (
          this.filterType === 'carAge' &&
          value.timeRangeSelector &&
          value.timeRangeSelector.length
        )
      )
    }
  },
  created () {
    this.localValue = this.value
    this.id = Math.floor(Math.random() * 10000000)
    if (this.defaultActive) {
      this.filterEnabled = true
    }
  },
  computed: {
    validationRules () {
      let rules = []
      if (this.filterEnabled) {
        switch (this.filterType) {
          case 'included':
            rules = ['required']
            break
          case 'range':
            rules = ['required']
            if (this.filterMode === 'remove-only' && this.originalValue) {
              rules.push({ name: 'is-in-range', param: { from: this.originalValue.kmFrom, to: this.originalValue.kmTo } })
            } else {
              rules.push('range')
            }
            break
          case 'exact':
            rules = ['required']
            break
        }
      }
      return rules
    }
  }
}
</script>
<style lang="scss" scoped>
.row {
  display: flex;
}
</style>
